import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Tag } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved,
  faCircleCheck,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// COMPONENTS
import DrawerRowDivider from "../components/drawer/DrawerRowDivider";
import MFAEnrollmentModal from "../authentication/MFAEnrollmentModal";
// INTERFACES
import { RootState } from "../../app/store";

/**
 * Treat as offline when build env says OFFLINE OR when an offlineToken is
 * present in localStorage (i.e. user logged in via the Offline page even
 * though `npm run start:local` set REACT_APP_ENVIRONMENT to DEVELOPMENT).
 * Without this, localhost users running `start:local` against a local
 * backend would see the Cognito-only "explanatory note" branch instead of
 * the local TOTP enrollment QR — and Authy/Google-Authenticator would have
 * no client secret to scan.
 */
function isOfflineMode(): boolean {
  if (process.env.REACT_APP_ENVIRONMENT === "OFFLINE") return true;
  try {
    return Boolean(localStorage.getItem("offlineToken"));
  } catch {
    return false;
  }
}
const IS_OFFLINE = isOfflineMode();

/**
 * MultiFactorAuth is the per-user MFA settings section of YourProfileDrawer.
 *
 * Two backends, one section:
 *   - OFFLINE / edge: enrolls a TOTP secret via /api/mfa/enroll/init (the
 *     local LocalTOTPVerifier verifies it). User scans the QR rendered by
 *     MFAEnrollmentModal, types the first code, MFAEnrolled flips to true.
 *   - CLOUD / Cognito: TOTP enrollment is owned by Cognito's
 *     SOFTWARE_TOKEN_MFA flow (see CustomAmplifyMFASetUp). This section
 *     shows status and an explanatory note rather than offering local
 *     enrollment — mixing the two would create two TOTP secrets per user
 *     and confuse authenticator apps.
 *
 * Status reflects `userLogged.mfa_enrolled`, which the backend returns on
 * /api/users/me. After successful enrollment we don't re-fetch — the
 * MFAEnrollmentModal's onEnrolled callback flips a local flag so the UI
 * updates immediately; the next /me fetch reconciles permanently.
 */
function MultiFactorAuth() {
  const [t] = useTranslation("global");
  const { theme, userLogged } = useSelector((state: RootState) => state.home);

  const [modalOpen, setModalOpen] = useState(false);
  const [justEnrolled, setJustEnrolled] = useState(false);

  const enrolled = (userLogged && userLogged.mfa_enrolled) || justEnrolled;

  const onClickEnroll = () => {
    setModalOpen(true);
  };

  const onModalClose = () => {
    setModalOpen(false);
  };

  const onEnrolled = () => {
    setJustEnrolled(true);
  };

  return (
    <div>
      <br />
      <DrawerRowDivider icon={faShieldHalved} title={t("authentication.multiFactorAuth")} />
      <div className={`drawer__box__${theme}`}>
        <div
          className="generalStyles__flex"
          style={{ alignItems: "center", justifyContent: "space-between", padding: "8px 0" }}
        >
          <div>
            <p className="drawer__title" style={{ marginBottom: "4px" }}>
              {t("authentication.totpStatus")}
            </p>
            {enrolled ? (
              <Tag
                color="success"
                icon={<FontAwesomeIcon icon={faCircleCheck} className="generalStyles__mrFix" />}
              >
                {t("authentication.mfaEnrolled")}
              </Tag>
            ) : (
              <Tag
                color="warning"
                icon={
                  <FontAwesomeIcon icon={faCircleExclamation} className="generalStyles__mrFix" />
                }
              >
                {t("authentication.mfaNotEnrolled")}
              </Tag>
            )}
          </div>
          <div>
            {IS_OFFLINE ? (
              <Button onClick={onClickEnroll} className="buttonStyle__1">
                {enrolled ? t("authentication.reenrollMfa") : t("authentication.enableMfa")}
              </Button>
            ) : (
              <Tag color="processing">{t("authentication.mfaManagedByCognito")}</Tag>
            )}
          </div>
        </div>
        <p className="drawer__text" style={{ marginTop: "8px", fontSize: "12px" }}>
          {IS_OFFLINE ? t("authentication.mfaOfflineHint") : t("authentication.mfaCognitoHint")}
        </p>
      </div>
      <MFAEnrollmentModal
        open={modalOpen}
        onClose={onModalClose}
        onEnrolled={onEnrolled}
        alreadyEnrolled={Boolean(enrolled)}
      />
    </div>
  );
}

export default MultiFactorAuth;
